import React from 'react';
import { Dialog, Transition } from '@headlessui/react';

interface TimeRangeOption {
  value: string;
  label: string;
}

interface TimeRangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  options: TimeRangeOption[];
  selectedValue: string;
  onSelectTimeRange: (value: string) => void;
  timeZone?: string;
  onTimeZoneSelect?: (value: string) => void;
  assetTimeZone?: string | null;
  timeZoneMode?: "local" | "asset" | "UTC";
}

const TimeRangeModal: React.FC<TimeRangeModalProps> = ({
  isOpen,
  onClose,
  options,
  selectedValue,
  onSelectTimeRange,
  timeZone = 'local',
  onTimeZoneSelect,
  assetTimeZone = null,
  timeZoneMode,
}) => {
  if (!isOpen) return null;
  const localTimeZone =
    typeof Intl !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : "UTC";
  const resolvedAssetTimeZone = assetTimeZone || null;
  // Determine which mode is active: prefer explicit mode, otherwise infer from timeZone string
  const derivedMode =
    timeZoneMode ??
    (timeZone === "UTC"
      ? "UTC"
      : resolvedAssetTimeZone && timeZone === resolvedAssetTimeZone
      ? "asset"
      : timeZone === localTimeZone
      ? "local"
      : "local");
  const isLocalSelected = derivedMode === "local";
  const isAssetSelected = derivedMode === "asset";
  const isUTCSelected = derivedMode === "UTC";

  return (
    <Transition appear show={isOpen} as={React.Fragment}>
      <Dialog as="div" className="relative z-[10050]" onClose={onClose}>
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 shadow-xl transition-all">
                <div className="flex flex-col gap-4">
                  <div className="flex justify-between items-center border-b pb-2">
                    <Dialog.Title className="text-lg font-semibold">
                      Select Time Range
                    </Dialog.Title>
                    <button
                      onClick={onClose}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="flex flex-col gap-2 mt-2">
                    {options.map((option) => (
                      <button
                        key={option.value}
                        onClick={() => {
                          onSelectTimeRange(option.value);
                          // Don't close automatically to let users select time zone too
                        }}
                        className={`flex items-center px-3 py-3 rounded-md transition-colors ${
                          selectedValue === option.value
                            ? 'bg-blue-50 border border-blue-500 text-blue-700'
                            : 'bg-gray-50 border border-gray-300 text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="16" 
                          height="16" 
                          fill="none" 
                          viewBox="0 0 24 24" 
                          stroke="currentColor" 
                          className="mr-3 text-gray-600"
                        >
                          <path 
                            strokeLinecap="round" 
                            strokeLinejoin="round" 
                            strokeWidth={2} 
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" 
                          />
                        </svg>
                        <span className="flex-1 text-sm text-left font-medium">{option.label}</span>
                      </button>
                    ))}
                    
                    {/* Time Zone Selection */}
                    <>
                      <div className="mt-4 mb-1">
                        <h3 className="text-md font-semibold text-gray-700">Time Zone</h3>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          onClick={() => onTimeZoneSelect && onTimeZoneSelect('local')}
                          className={`px-3 py-2 rounded-md transition-colors text-center text-sm ${
                            isLocalSelected
                              ? 'bg-blue-50 border border-blue-500 text-blue-700'
                              : 'bg-gray-50 border border-gray-300 text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          Local
                        </button>
                        <button
                          onClick={() => onTimeZoneSelect && onTimeZoneSelect('asset')}
                          className={`px-3 py-2 rounded-md transition-colors text-center text-sm ${
                            isAssetSelected
                              ? 'bg-blue-50 border border-blue-500 text-blue-700'
                              : 'bg-gray-50 border border-gray-300 text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          Asset
                        </button>
                        <button
                          onClick={() => onTimeZoneSelect && onTimeZoneSelect('UTC')}
                          className={`px-3 py-2 rounded-md transition-colors text-center text-sm ${
                            isUTCSelected
                              ? 'bg-blue-50 border border-blue-500 text-blue-700'
                              : 'bg-gray-50 border border-gray-300 text-gray-600 hover:bg-gray-100'
                          }`}
                        >
                          UTC
                        </button>
                      </div>
                    </>
                  </div>
                  
                  <div className="flex justify-end pt-4 gap-2">
                    <button
                      onClick={onClose}
                      className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={onClose}
                      className="eai-btn-grn"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default TimeRangeModal;
